import z from 'zod'
import {
  nullSafeBoolean,
  nullSafeNumber,
  nullSafeOptionalStringArray,
  nullSafeStringArray,
} from '../shared/schema'
import { SessionMuting, RoomStateVideoMethod } from '../../types'

const sessionMutingSchema = z.nativeEnum(SessionMuting)
const videoMethodSchema = z.nativeEnum(RoomStateVideoMethod)

const automaticRoomNameValuesSchema = z
  .object({
    adjectives: nullSafeStringArray([]),
    nouns: z.record(z.string(), z.string()),
  })
  .optional()

export const schema = z.object({
  adminRoleManagementEnabled: nullSafeBoolean(false),
  automaticRoomNameValues: z.preprocess((val) => {
    if (val) {
      const parsed = automaticRoomNameValuesSchema.safeParse(val)
      // if it's correctly formatted, use it
      // in any other case, undefined
      if (parsed.success) return parsed.data
    }

    return undefined
  }, automaticRoomNameValuesSchema),
  allowDraftAssignments: nullSafeBoolean(false),
  allowEmailUpdate: nullSafeBoolean(false),
  allowPublishedSlideDeckEdit: nullSafeBoolean(false),
  animationMethod: nullSafeBoolean(true),
  automatedRandomGrouping: nullSafeBoolean(true),
  generateGoogleSlides: nullSafeBoolean(false),
  generateSessionSummary: nullSafeBoolean(true),
  rubricTestDesiredStdDeviation: nullSafeNumber(1.5),
  rubricTestMeanEpsilon: nullSafeNumber(0.9),
  sendInstructorEmails: nullSafeBoolean(false),
  sessionMuting: z.preprocess(
    (val) => val ?? SessionMuting.none,
    sessionMutingSchema
  ),
  sessionResultsGroupBalanceCoefficient: nullSafeNumber(0.5),
  sessionResultsShowAllConversationTooltips: nullSafeBoolean(true),
  showRubricArgumentEventMatchMinimum: nullSafeNumber(1), // float value
  studentLibraryUseNew: nullSafeBoolean(false),
  useStrictPublishRequirements: nullSafeBoolean(false),
  videoMethod: z.preprocess(
    (val) => val ?? RoomStateVideoMethod.broadcast,
    videoMethodSchema
  ),
  showOrganizationsUI: nullSafeBoolean(false),
  avTroubleshootingEnabled: nullSafeBoolean(true),
  meetingFeedbackAsStarsRatio: nullSafeNumber(0),
  showNotifications: nullSafeBoolean(false),
  transcriptionLanguages: nullSafeOptionalStringArray(),
  transcriptProblemWaitDurationMS: nullSafeNumber(0), // in ms
  verifyInvitation: nullSafeBoolean(false),
  forceGradeExpiredRooms: nullSafeBoolean(false),
  forceLoginAccountSelect: nullSafeBoolean(false),
  showAssignmentCSVResultsInMinutesAndSortingAsPercent: nullSafeBoolean(false),
  connectionQualityIndicators: nullSafeBoolean(false),
  showInstructorConversationsTab: nullSafeBoolean(false),
  demoVideoMethod: z.preprocess((val) => {
    // this is protection against bad data - if it's streaming,
    // use it, otherwise default to broadcast
    if (val === RoomStateVideoMethod.streaming) return val
    return RoomStateVideoMethod.broadcast
  }, videoMethodSchema),
  figJamSlides: nullSafeBoolean(false),
  // timeout in seconds, cannot be negative
  soloBotNudgeButtonTimeout: nullSafeNumber(0).transform((val) =>
    val < 0 ? 0 : val
  ),
  hideSlideDeckPrice: nullSafeBoolean(false),
  enableMeetingPaywall: nullSafeBoolean(false),
  useNextbookImportForm: nullSafeBoolean(false),
  enhancedSupportLinkParams: nullSafeBoolean(true),
  showApplyGroupsToFutureAssignments: nullSafeBoolean(false),
  assignmentOutputsEnabled: nullSafeBoolean(false),
  allowSlideDeckRoleCreation: nullSafeBoolean(false),
  avCheckUseForcedUpdate: nullSafeBoolean(false),
  useSharedGoogleDocSlide: nullSafeBoolean(false),
  slideDeckSwapEnabled: nullSafeBoolean(false),
  serviceWorkerKillSwitch: nullSafeBoolean(false),
})

export const empty = Object.freeze(schema.parse({}))

export type FirestoreSettingsFeatureFlags = z.infer<typeof schema>
